import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/components/site/assets";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/platform", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/platform", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO} alt="JJ Speed Factory" className="h-14 w-auto object-contain mb-6" />
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-white/40">
            Athlete Platform
          </span>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mt-3">
            Sign In
          </h1>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">
              Email or Username
            </label>
            <input
              data-testid="login-email"
              type="text"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#121212] border border-white/10 focus:border-white outline-none px-4 py-3 text-sm transition-colors"
            />
          </div>
          <div>
            <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#121212] border border-white/10 focus:border-white outline-none px-4 py-3 text-sm transition-colors"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm py-3.5 hover:bg-white/85 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <a
          href="/"
          data-testid="login-back-home"
          className="block text-center mt-8 font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/30 hover:text-white/60 transition-colors"
        >
          ← Back to Website
        </a>

        <div className="mt-10 border-t border-white/10 pt-8" data-testid="login-roles">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4 text-center">
            Who Logs In Here
          </p>
          <ul className="space-y-2 text-sm text-white/55 mb-6">
            <li className="flex gap-2"><span className="text-white/80 font-bold">Admin / Coach</span> — sign in above with your email &amp; password.</li>
            <li className="flex gap-2"><span className="text-white/80 font-bold">Athletes</span> — sign in with the <span className="text-white/80">username &amp; password</span> your coach gave you, or your email if you activated online.</li>
          </ul>
          <p className="text-white/45 text-xs mb-4 leading-relaxed">
            New here? Accounts are created once you book and complete a free evaluation. Start below and Coach Johnson will get you set up.
          </p>
          <a
            href="/#assessment"
            data-testid="login-book-evaluation"
            className="w-full inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bold uppercase tracking-widest text-xs py-3.5 hover:bg-white hover:text-black transition-colors"
          >
            Book a Free Evaluation
          </a>
        </div>
      </div>
    </div>
  );
}
